import React, { useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { SendIcon, PaperclipIcon, CloseIcon, FileTextIcon } from "./Icons.tsx";

interface ChatInputProps {
  query: string;
  setQuery: (query: string) => void;
  handleSubmit: (e?: React.FormEvent, queryOverride?: string) => void;
  isLoading: boolean;
  isSearched: boolean;
  selectedFile?: File | null;
  setSelectedFile?: (file: File | null) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  query,
  setQuery,
  handleSubmit,
  isLoading,
  isSearched,
  selectedFile = null,
  setSelectedFile,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea height as the user types
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate scrollHeight properly
    textarea.style.height = "auto";

    // Set new height based on content scroll height
    const scrollHeight = textarea.scrollHeight;
    if (query) {
      textarea.style.height = `${scrollHeight}px`;
    }
  }, [query]);

  // Handle Enter to submit, and Shift+Enter to newline
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (acceptedFiles.length > 0 && acceptedFiles[0]) {
      setSelectedFile?.(acceptedFiles[0]);
    }
    if (fileRejections.length > 0 && fileRejections[0]) {
      const error = fileRejections[0].errors[0];
      if (error?.code === "file-too-large") {
        alert(t("common.file_size_error", "Üleslaaditud fail on liiga suur. Maksimaalne lubatud suurus on 10MB."));
      } else {
        alert(t("common.file_type_error", "Valitud failitüüp ei ole toetatud. Palun laadi üles ainult pilt või PDF."));
      }
    }
  }, [setSelectedFile, t]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    disabled: isLoading,
  });

  return (
    <div
      {...getRootProps()}
      className={`w-full transition-all rounded-3xl relative ${isDragActive ? "ring-4 ring-blue-500/20 border-blue-500 scale-[1.01]" : ""}`}
    >
      {isDragActive && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-blue-500/10 dark:bg-blue-500/5 backdrop-blur-xs border-2 border-dashed border-blue-500 rounded-3xl pointer-events-none animate-message">
          <span className="text-sm sm:text-base font-bold text-blue-600 dark:text-blue-400 flex items-center gap-2">
            <PaperclipIcon className="w-5 h-5 animate-bounce" />
            {t("common.drop_file_here", "Lohista pilt või PDF siia üleslaadimiseks")}
          </span>
        </div>
      )}
      <form onSubmit={e => handleSubmit(e)} className="flex flex-col w-full relative">
        <input {...getInputProps()} />

        {selectedFile && (
          <div className="flex items-center gap-3 p-3 mb-3 rounded-2xl bg-slate-50 dark:bg-slate-900/80 border border-slate-200/40 dark:border-slate-800/40 animate-message">
            <div className="p-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl">
              <FileTextIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-slate-400">
                {selectedFile.type || t("common.document", "Dokument")}
                {" "}
                •
                {(selectedFile.size / 1024).toFixed(1)}
                {" "}
                KB
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedFile?.(null)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
              title={t("common.remove_file", "Eemalda fail")}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="relative group flex items-center w-full">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 flex items-center z-10">
            <button
              type="button"
              onClick={open}
              disabled={isLoading}
              className="flex items-center justify-center p-2 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 rounded-xl transition-all duration-300 border border-slate-200/60 dark:border-slate-700/50 shadow-xs active:scale-95 disabled:opacity-50 shrink-0 cursor-pointer"
              title={t("common.upload_file", "Laadi fail üles")}
            >
              <PaperclipIcon className="w-4 h-4" />
            </button>
          </div>
          <textarea
            id="search-input"
            ref={textareaRef}
            rows={1}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isSearched ? t("common.input_placeholder_followup", "Esita lisaküsimus või täpsusta...") : t("common.input_placeholder", "Kirjutage oma juriidiline küsimus siia...")}
            aria-label={t("common.input_aria_label", "Juriidiline otsing")}
            className="w-full pl-14 sm:pl-16 pr-14 sm:pr-16 py-5 sm:py-6 text-base sm:text-lg md:text-xl rounded-[22px] sm:rounded-3xl border-2 border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 shadow-2xl shadow-slate-200/50 dark:shadow-none transition-all dark:text-white placeholder:text-slate-400 resize-none overflow-y-auto max-h-36 sm:max-h-48 leading-relaxed align-bottom"
          />
          <button
            type="submit"
            disabled={(!query.trim() && !selectedFile) || isLoading}
            className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl sm:rounded-2xl disabled:opacity-50 disabled:hover:bg-blue-600 transition-all shadow-lg shadow-blue-600/20 active:scale-95 cursor-pointer"
          >
            <SendIcon />
          </button>
        </div>
      </form>
    </div>
  );
};
